import { NgRedux, select } from "@angular-redux/store";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { TranslationService } from "../../../common/services/languages/translation.service";
import { SnackBarService } from "../../../common/services/snack-bar/snack-bar.service";
import { ApiResponseActions } from "../../../store/api-response/api-response-actions";
import { ApiResponseDispatcher } from "../../../store/api-response/api-response-dispatcher";
import { ApiResponseState } from "../../../store/api-response/api-response-state.model";
import { AppState } from "../../../store/state.model";
import { OrchestrationService } from "../../services/orchestration-service/orchestration.service";
import {
  SpotifyEligibleGenerations,
  AmazonMusicEligibleGenerations,
} from "../../../common/constants/constant";
import { SelectedVehicleState } from "../../../store/selected-vehicle-store/selected-vehicle-state.model";
import { NgIf } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "app-linked-accounts",
    templateUrl: "./linked-accounts.component.html",
    styleUrls: ["./linked-accounts.component.scss"],
    imports: [NgIf, MatIcon, TranslatePipe]
})
export class LinkedAccountsComponent implements OnInit, OnDestroy {
  public hasError: boolean = false;
  /**
   * @property Property to store guid
   */
  public guid: string;
  /**
   * @property Property to store the api subscription
   */
  public apiSubscription: Subscription;
  /**
   * @property Property to store the api subscription for selectedVehicle
   */
  public selectedVehicleSubscripion: Subscription;
  /**
   * Property to observe the redux customer store
   **/
  @select("api") readonly apiObservable: Observable<ApiResponseState>;
  /**
   * Property to store linked accounts response
   **/
  public linkedAccounts: {
    amazonMusic: boolean;
    spotify: boolean;
  };
  /**
   * @property used to store the vehicle generation
   */
  public vehicleGeneration: string;
  /**
   * @property used to store the spotify to be enabled /disabled
   */
  public enableSpotify = false;

  /**
   * @property used to store the amazon music to be enabled /disabled
   */
  public enableAmazonMusic = false;

  /**
   * @property removeVehicle to store the vehicle to remove from account
   */
  public selectedVehicle: object;

  /**
   *@arg selectedVehicleObservable to observe selectedVehicle observable
   **/
  @select("selectedVehicle")
  readonly selectedVehicleObservable: Observable<SelectedVehicleState>;

  constructor(
    private snackBarService: SnackBarService,
    private orch: OrchestrationService,
    public ngRedux: NgRedux<AppState>,
    private apiDispatcher: ApiResponseDispatcher,
    public translate: TranslationService
  ) {
    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (api.apiAction === ApiResponseActions.SET_LINKED_ACCOUNTS) {
        api.apiResponse["type"] === "success" && api.apiResponse["payload"] && this.linkedAccountsSuccess(api.apiResponse["payload"]);
        api.apiResponse["type"] === "error" && this.linkedAccountsError(api.apiResponse);
      }
    });
  }
  /**
   * init methods
   */
  ngOnInit(): void {
    this.selectedVehicleSubscripion = this.selectedVehicleObservable.subscribe(
      ({ vehicle }) => {
        if (!vehicle) {
          return;
        }
        this.selectedVehicle = vehicle;
        const state = this.ngRedux.getState();
        const customerData =
          state && state.customer && state.customer.data
            ? state.customer.data
            : {};

        this.guid =
          customerData && customerData["guid"]
            ? customerData["guid"]
            : vehicle["subscriberGuid"]
            ? vehicle["subscriberGuid"]
            : "";

        if (!!this.guid && vehicle["vin"]) {
          this.orch.getLinkedAccounts(vehicle["vin"]);
        }

        this.vehicleGeneration = vehicle["generation"] || "";
        this.enableSpotify =
          (!!this.vehicleGeneration &&
            SpotifyEligibleGenerations.includes(
              this.vehicleGeneration.toLowerCase()
            )) ||
          this.vehicleGeneration === null ||
          this.vehicleGeneration === undefined;

        this.enableAmazonMusic =
          (!!this.vehicleGeneration &&
            AmazonMusicEligibleGenerations.includes(
              this.vehicleGeneration.toLowerCase()
            )) ||
          this.vehicleGeneration === null ||
          this.vehicleGeneration === undefined;
      }
    );
  }
  ngOnDestroy(): void {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
    if (this.selectedVehicleSubscripion) {
      this.selectedVehicleSubscripion.unsubscribe();
    }
  }

  /**
   * success callback of linked accounts api call
   * @param payload payload from api
   */
  private linkedAccountsSuccess(payload: Array<string>): void {
    this.linkedAccounts = {
      amazonMusic: payload.includes("amazon"),
      spotify: payload.includes("spotify"),
    };
  }
  /**
   * error callback of linked accounts api call
   * @param error error object from api
   */
  private linkedAccountsError(error: object): void {
    this.hasError = true;
    this.apiDispatcher.setLinkedAccounts({});
    this.snackBarService.showToast("Failed to load Linked Accounts.");
  }
}
